import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SyncIcon from "@mui/icons-material/Sync";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { calculateUnitCost } from "../../../utils/costUtils";
import CostProfileRowDetails from "./CostProfileRowDetails";

const SectionHeader = ({ title }) => (
  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, mt: 2 }}>
    {title}
  </Typography>
);

const TypeChip = ({ isComponent }) => (
  <Chip
    size="small"
    variant="outlined"
    label={isComponent ? "Component" : "Product"}
  />
);

const CostProfileCardList = ({ profiles, onEdit, onDelete, onSync }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all"); // "all" | "products" | "components"

  const { products, components } = useMemo(() => {
    const comps = [];
    const prods = [];
    (profiles || []).forEach((p) =>
      p.isComponent ? comps.push(p) : prods.push(p)
    );
    return { products: prods, components: comps };
  }, [profiles]);

  const showProducts = filter === "all" || filter === "products";
  const showComponents = filter === "all" || filter === "components";

  const FilterChips = () => {
    const makeVariant = (k) => (filter === k ? "filled" : "outlined");
    return (
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
        <Chip
          label="All"
          variant={makeVariant("all")}
          onClick={() => setFilter("all")}
        />
        <Chip
          label="Products"
          variant={makeVariant("products")}
          onClick={() => setFilter("products")}
        />
        <Chip
          label="Components"
          variant={makeVariant("components")}
          onClick={() => setFilter("components")}
        />
      </Stack>
    );
  };

  const renderCard = (p) => {
    const unitCost = calculateUnitCost(p);
    const isOpen = expandedId === p._id;
    const margin =
      !p.isComponent && p.unitPrice
        ? ((Number(p.unitPrice) - unitCost) / Number(p.unitPrice)) * 100
        : null;

    return (
      <Card key={p._id} sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography fontWeight={600}>{p.name}</Typography>
              <TypeChip isComponent={p.isComponent} />
            </Stack>
            <IconButton
              onClick={() => setExpandedId(isOpen ? null : p._id)}
              size="small">
              {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Type: {p.type}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Batch: {p.batchSize || "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unit Price:{" "}
            {p.isComponent ? "—" : `$${Number(p.unitPrice || 0).toFixed(2)}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Unit Cost: ${unitCost.toFixed(2)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Margin: {margin !== null ? `${margin.toFixed(1)}%` : "—"}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Stack direction="row" spacing={2}>
            <Tooltip title="Edit">
              <IconButton onClick={() => onEdit(p)} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Sync">
              <IconButton onClick={() => onSync(p)} size="small">
                <SyncIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Delete">
              <IconButton
                onClick={() => onDelete(p)}
                size="small"
                color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Collapse in={isOpen}>
            <Box mt={2}>
              <CostProfileRowDetails profile={p} open />
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <FilterChips />

      {showComponents && components.length > 0 && (
        <>
          <SectionHeader title="Reusable Components" />
          {components.map(renderCard)}
          {showProducts && products.length > 0 && <Divider sx={{ my: 2 }} />}
        </>
      )}

      {showProducts && products.length > 0 && (
        <>
          <SectionHeader title="Product Cost Profiles" />
          {products.map(renderCard)}
        </>
      )}

      {showProducts &&
        products.length === 0 &&
        showComponents &&
        components.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 2 }}>
            No cost profiles found.
          </Typography>
        )}
    </Box>
  );
};

export default CostProfileCardList;
